/**
 * Primitivos visuais compartilhados do módulo Análise Kaizen.
 */
import React from 'react';

export const CARD_TITLE_CLASS = 'text-[10px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100';
export const PANEL_CLASS = 'rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] overflow-hidden';
export const PANEL_HEADER_CLASS = 'flex items-center justify-between border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80 px-4 py-2.5';
export const PANEL_HEADER_COMPACT = 'flex items-center justify-between border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/80 px-3 py-1.5';
export const CHART_BODY_CLASS = 'px-4 py-3';
export const CHART_BODY_COMPACT = 'px-3 py-2';
export const KAIZEN_GAP = 'gap-1.5';

/**
 * Moldura periférica do shell Análise — lateral e base com o mesmo token (px-5 = pb-5).
 * Usar no container pai das três sub-abas; altura via flex-1/min-h-0 (sem calc dvh).
 */
export const KAIZEN_SHELL_FRAME = 'px-5 pt-1 pb-5';

/** Moldura da aba Benchmark — pb-6 simétrico ao px-5 lateral. */
export const BENCHMARK_SHELL_FRAME = 'px-5 pt-3 pb-6';

/** Linha 2 colunas com altura igual (stretch). */
export const KAIZEN_STRETCH_ROW = `grid min-h-0 flex-1 grid-cols-1 items-stretch ${KAIZEN_GAP} lg:grid-cols-2`;

/** Grade 2×2 preenchendo o espaço vertical restante. */
export const KAIZEN_QUAD_GRID = `grid min-h-0 flex-1 grid-cols-1 grid-rows-2 items-stretch ${KAIZEN_GAP} lg:grid-cols-2`;

/** Margens Recharts — reserva para rótulos de eixo sem cortar. */
export const RECHART_MARGIN = {
  line: { top: 8, right: 8, left: -12, bottom: 24 },
  bar: { top: 8, right: 8, left: -8, bottom: 24 },
  barVertical: { top: 4, right: 18, left: 32, bottom: 24 },
  composed: { top: 8, right: 28, left: 0, bottom: 28 },
  area: { top: 8, right: 8, left: 4, bottom: 24 },
} as const;

/** Painel lateral esticável (tabelas / KPIs) — mesma altura que ChartBlock fill. */
export const KAIZEN_STRETCH_PANEL = `${PANEL_CLASS} flex h-full min-h-0 flex-col`;
export const CHART_GRID = 'var(--border-default)';
export const CHART_TICK = 'var(--text-secondary)';
export const ACCENT_BLUE = '#6366F1';
export const ACCENT_BLUE_SOFT = '#818CF8';
export const CHART_BAR = '#6366F1';
export const CHART_ALERT = '#F59E0B';
export const EMERALD_MAIN = '#10B981';
export const INDIGO_MAIN = '#6366F1';
export const EMPTY_OVERLAY_MSG = 'Sem dados para o período selecionado';

export type KaizenSubTab = 'value-flow' | 'losses' | 'financial';

export function buildZeroDaySeries(days: number): Array<{ date: string; label: string; value: number; total: number }> {
  const formatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
  const today = new Date();
  const series: Array<{ date: string; label: string; value: number; total: number }> = [];

  for (let index = days - 1; index >= 0; index -= 1) {
    const current = new Date(today);
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() - index);
    series.push({
      date: current.toISOString().slice(0, 10),
      label: formatter.format(current),
      value: 0,
      total: 0,
    });
  }

  return series;
}

export function hasSeriesActivity<T extends {
  value?: number;
  total?: number;
  count?: number;
  economia?: number;
  custo?: number;
  cumulativeSavings?: number;
}>(rows: T[] | undefined): boolean {
  if (!rows?.length) return false;
  return rows.some((row) =>
    (row.total ?? 0) > 0
    || (row.value ?? 0) > 0
    || (row.count ?? 0) > 0
    || (row.economia ?? 0) > 0
    || (row.custo ?? 0) > 0
    || (row.cumulativeSavings ?? 0) > 0,
  );
}

export function ChartPlotArea({
  heightClass = 'h-[140px]',
  fill = false,
  hasActivity,
  emptyMessage = EMPTY_OVERLAY_MSG,
  children,
}: {
  heightClass?: string;
  fill?: boolean;
  hasActivity: boolean;
  emptyMessage?: string;
  children: React.ReactNode;
}) {
  const sizeClass = fill ? 'min-h-0 flex-1' : heightClass;
  return (
    <div className={`relative w-full ${sizeClass}`}>
      {children}
      {!hasActivity && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <span className="rounded-sm border border-[var(--border-default)]/50 bg-[var(--bg-surface)]/85 px-3 py-1.5 text-[10px] font-medium text-[var(--text-muted)] shadow-sm backdrop-blur-[2px]">
            {emptyMessage}
          </span>
        </div>
      )}
    </div>
  );
}

export function PremiumTooltip({
  active,
  payload,
  label,
  formatter,
}: {
  active?: boolean;
  payload?: Array<{ value?: number; payload?: Record<string, unknown>; dataKey?: string }>;
  label?: string;
  formatter?: (value: number, payload?: Record<string, unknown>) => { main: string; sub: string } | string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  const rawValue = typeof point.value === 'number' ? point.value : 0;
  const formatted = formatter ? formatter(rawValue, point.payload) : { main: String(rawValue), sub: '' };
  const mainText = typeof formatted === 'string' ? formatted : formatted.main;
  const subText = typeof formatted === 'string' ? '' : formatted.sub;

  return (
    <div className="rounded-[6px] border border-[var(--border-default)] bg-[#0F172A] px-4 py-3 text-[11px] shadow-lg backdrop-blur-md">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">{label}</div>
      <div className="mb-0.5 text-[14px] font-bold text-white">{mainText}</div>
      {subText ? <div className="text-[10px] text-slate-400">{subText}</div> : null}
    </div>
  );
}

export function ChartBlock({
  title,
  icon,
  subtitle,
  hasActivity,
  heightClass,
  fill = false,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  subtitle?: string;
  hasActivity: boolean;
  heightClass?: string;
  fill?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`${PANEL_CLASS} flex flex-col ${fill ? 'h-full min-h-0 flex-1' : ''}`}>
      <div className={`flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-default)]/50 ${fill ? 'px-3 py-1.5' : 'px-4 py-2'}`}>
        <div className="flex items-center gap-1.5">
          {icon}
          <span className={CARD_TITLE_CLASS}>{title}</span>
        </div>
        {subtitle ? (
          <span className="text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">{subtitle}</span>
        ) : null}
      </div>
      <div className={`${fill ? CHART_BODY_COMPACT : 'px-3 py-1.5'} ${fill ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
        <ChartPlotArea heightClass={heightClass} fill={fill} hasActivity={hasActivity}>
          {children}
        </ChartPlotArea>
      </div>
    </div>
  );
}

export function formatLeadTimeHours(minutes?: number): string {
  if (!minutes || minutes <= 0) return '0h';
  return `${Math.round(minutes / 60)}h`;
}

export function formatPercent(value?: number | null): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

/** Viewport útil da aba Análise — encaixa numa tela sem scroll. */
export const KAIZEN_VIEWPORT_CLASS = 'h-full max-h-full overflow-hidden';
